import React, { useState } from 'react';
import { AnimatePresence, motion, Variants } from 'framer-motion';
import { jobs } from '../data/experience';
import { Parallax, Reveal } from './motion/Reveal';

const slideVariants: Variants = {
  enter: (direction: number) => ({ x: direction > 0 ? 90 : -90, opacity: 0 }),
  center: {
    x: 0,
    opacity: 1,
    transition: { x: { type: 'spring', stiffness: 300, damping: 30 }, opacity: { duration: 0.2 } },
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -90 : 90,
    opacity: 0,
    transition: { x: { type: 'spring', stiffness: 300, damping: 30 }, opacity: { duration: 0.2 } },
  }),
};

const swipeConfidenceThreshold = 10000;

const Experience: React.FC = () => {
  const [[index, direction], setPage] = useState<[number, number]>([0, 0]);
  const job = jobs[index];

  const paginate = (dir: number) => {
    setPage(([i]) => [(i + dir + jobs.length) % jobs.length, dir]);
  };

  const goTo = (i: number) => {
    setPage(([current]) => (i === current ? [current, 0] : [i, i > current ? 1 : -1]));
  };

  return (
    <section
      className="bg-grey-50 py-16 md:py-20"
      id="experience"
    >
      <div className="container">
      <Reveal>
        <Parallax>
          <h2 className="text-center font-header text-4xl font-semibold uppercase sm:text-5xl lg:text-6xl bg-gradient-to-r from-primary via-blue-400 to-primary bg-[length:200%_auto] bg-clip-text text-transparent motion-safe:animate-shimmer">
            My work experience
          </h2>
        </Parallax>
      </Reveal>

      <Reveal delay={0.1}>
        <div
          role="region"
          aria-roledescription="carousel"
          aria-label="Work experience"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') paginate(-1);
            if (e.key === 'ArrowRight') paginate(1);
          }}
          className="relative mx-auto mt-12 w-full focus:outline-none lg:w-2/3"
        >
          {/* Scrubber: horizontal timeline doubling as pagination */}
          <div className="relative overflow-x-auto pb-2">
            <div className="relative flex min-w-max justify-between gap-6 px-2 sm:min-w-0 sm:gap-0">
              {/* Track + progress fill (fill needs the evenly spaced sm:flex-1 columns) */}
              <span aria-hidden="true" className="absolute inset-x-2 top-1.5 h-0.5">
                <span className="absolute inset-0 bg-grey-40/40"></span>
                <motion.span
                  className="absolute inset-y-0 left-0 hidden bg-gradient-to-r from-primary to-blue-400 sm:block"
                  animate={{ width: `${((index + 0.5) / jobs.length) * 100}%` }}
                  transition={{ type: 'spring', stiffness: 200, damping: 30 }}
                />
              </span>
              {jobs.map((j, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  aria-label={`${j.startYear} — ${j.company}`}
                  aria-current={i === index}
                  className="group relative flex flex-col items-center sm:flex-1"
                >
                  <span className="relative flex h-3 w-3 items-center justify-center">
                    {i === index && (
                      <span
                        aria-hidden="true"
                        className="absolute inset-0 rounded-full bg-primary/60 motion-safe:animate-pulse-ring"
                      ></span>
                    )}
                    <span
                      className={`relative z-10 h-3 w-3 rounded-full border-2 transition-all duration-300 group-hover:scale-125 ${
                        i === index
                          ? 'bg-primary border-primary'
                          : j.isCurrent
                            ? 'bg-white border-primary'
                            : 'bg-white border-grey-40 group-hover:border-primary'
                      }`}
                    ></span>
                  </span>
                  <span
                    className={`pt-2 font-body text-xs font-bold transition-colors duration-300 ${
                      i === index ? 'text-primary' : 'text-grey-40 group-hover:text-primary'
                    }`}
                  >
                    {j.startYear}
                  </span>
                  <span
                    className={`hidden text-xs transition-colors duration-300 sm:block ${
                      i === index ? 'text-primary' : 'text-grey-40 group-hover:text-primary'
                    }`}
                  >
                    {j.company}
                  </span>
                  {i === index && (
                    <motion.span
                      layoutId="active-job"
                      aria-hidden="true"
                      className="absolute -bottom-1 h-0.5 w-full bg-gradient-to-r from-primary to-blue-400"
                    />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Stage */}
          <div className="relative mt-8 min-h-[22rem]">
            <AnimatePresence initial={false} mode="popLayout" custom={direction}>
              <motion.div
                key={index}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.2}
                onDragEnd={(_, { offset, velocity }) => {
                  const swipe = Math.abs(offset.x) * velocity.x;
                  if (swipe < -swipeConfidenceThreshold) paginate(1);
                  else if (swipe > swipeConfidenceThreshold) paginate(-1);
                }}
                className="relative w-full cursor-grab overflow-hidden rounded-lg border border-grey-70 bg-white p-6 text-left shadow-xl shadow-primary/10 active:cursor-grabbing md:p-8"
              >
                <span
                  aria-hidden="true"
                  className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary via-blue-400 to-purple-500"
                ></span>
                <div className="flex flex-col justify-between gap-2 select-none sm:flex-row sm:items-center">
                  <span className="font-header text-2xl font-bold text-black">{job.company}</span>
                  <span className="self-start rounded-full bg-primary/10 px-3 py-1 font-body text-sm font-semibold text-primary sm:self-auto">
                    {job.duration}
                  </span>
                </div>
                <span className="block pt-2 font-header text-xl font-bold uppercase text-primary">
                  {job.role}
                  {job.isCurrent && (
                    <span className="ml-2 inline-block rounded-full bg-primary px-2 py-0.5 text-xs font-bold text-white uppercase align-middle">
                      Current
                    </span>
                  )}
                </span>
                <ul className="pt-4 space-y-2">
                  {job.bullets.map((bullet, i) => (
                    <li key={i} className="flex items-start gap-2 font-body text-black">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary"></span>
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </AnimatePresence>

            {/* Prev / Next arrows (desktop) */}
            <div className="absolute top-1/2 hidden -translate-y-1/2 md:-left-5 md:block lg:-left-16">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                aria-label="Previous role"
                onClick={() => paginate(-1)}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-primary shadow-lg"
              >
                <i className="bx bx-chevron-left text-3xl"></i>
              </motion.button>
            </div>
            <div className="absolute top-1/2 hidden -translate-y-1/2 md:-right-5 md:block lg:-right-16">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                aria-label="Next role"
                onClick={() => paginate(1)}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-primary shadow-lg"
              >
                <i className="bx bx-chevron-right text-3xl"></i>
              </motion.button>
            </div>
          </div>

          <p aria-live="polite" className="pt-4 text-center font-body text-sm text-grey-40">
            {index + 1} / {jobs.length} — {job.company}
          </p>
        </div>
      </Reveal>
      </div>
    </section>
  );
};

export default Experience;
